import React, { useState } from 'react';
import { FaSave, FaTimes, FaCheckCircle, FaFilePdf, FaUserTie, FaUsers, FaSignature } from 'react-icons/fa';

const DelawareCCorpChecklistForm = ({ initialData, onSave, onCancel, isSubmitting }) => {
  const defaultData = {
    fiscalYearEnd: '',
    inactivityFrom: '',
    inactivityTo: '',
    // Principal Place of Business
    principalStreet: '',
    principalCity: '',
    principalState: '',
    principalZip: '',
    principalCountry: 'USA',
    principalPhone: '',
    principalEmail: '',
    natureOfBusiness: '',
    // Officer Information
    officerFirstName: '',
    officerMiddleName: '',
    officerLastName: '',
    officerTitle: '',
    officerStreet: '',
    officerCity: '',
    officerState: '',
    officerZip: '',
    officerCountry: 'USA',
    // Directors
    directors: [{ firstName: '', middleName: '', lastName: '', street: '', city: '', state: '', zip: '', country: 'USA' }],
    // Authorization
    authFirstName: '',
    authMiddleName: '',
    authLastName: '',
    authTitle: '',
    authStreet: '',
    authCity: '',
    authState: '',
    authZip: '',
    authCountry: 'USA'
  };

  const [formData, setFormData] = useState({
    ...defaultData,
    ...(initialData || {})
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDirectorChange = (index, field, value) => {
    const newDirectors = [...formData.directors];
    newDirectors[index][field] = value;
    setFormData(prev => ({ ...prev, directors: newDirectors }));
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, { firstName: '', middleName: '', lastName: '', street: '', city: '', state: '', zip: '', country: 'USA' }]
    }));
  };

  const removeDirector = (index) => {
    if (formData.directors.length <= 1) return;
    const newDirectors = formData.directors.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, directors: newDirectors }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, true);
  };

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-dark-700">
      <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center bg-gradient-to-r from-indigo-600 to-violet-500">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FaCheckCircle /> Delaware C-CORP Annual Report Checklist
        </h2>
        <button onClick={onCancel} className="text-white/80 hover:text-white transition-colors">
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-10">
        {/* Section 1: Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">1</span>
              General Information
            </h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">End of Fiscal Year</label>
                <input type="text" name="fiscalYearEnd" value={formData.fiscalYearEnd} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. Dec 31" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Inactivity From</label>
                  <input type="date" name="inactivityFrom" value={formData.inactivityFrom} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Inactivity To</label>
                  <input type="date" name="inactivityTo" value={formData.inactivityTo} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center text-sm font-bold">2</span>
              Principal Place of Business
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Street Address</label>
                <input type="text" name="principalStreet" value={formData.principalStreet} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
                <input type="text" name="principalCity" value={formData.principalCity} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">State</label>
                <input type="text" name="principalState" value={formData.principalState} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zip Code</label>
                <input type="text" name="principalZip" value={formData.principalZip} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Country</label>
                <input type="text" name="principalCountry" value={formData.principalCountry} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Phone</label>
                <input type="text" name="principalPhone" value={formData.principalPhone} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Email</label>
                <input type="email" name="principalEmail" value={formData.principalEmail} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-1 pt-4">
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nature of Business</label>
          <textarea name="natureOfBusiness" value={formData.natureOfBusiness} onChange={handleChange} rows="2" className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
        </div>

        {/* Section 3: Officer Info */}
        <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center text-sm font-bold">3</span>
            Officer Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">First Name</label>
              <input type="text" name="officerFirstName" value={formData.officerFirstName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Middle Name</label>
              <input type="text" name="officerMiddleName" value={formData.officerMiddleName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Last Name</label>
              <input type="text" name="officerLastName" value={formData.officerLastName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Title</label>
              <input type="text" name="officerTitle" value={formData.officerTitle} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Street Address</label>
              <input type="text" name="officerStreet" value={formData.officerStreet} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
              <input type="text" name="officerCity" value={formData.officerCity} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">State</label>
              <input type="text" name="officerState" value={formData.officerState} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zip Code</label>
              <input type="text" name="officerZip" value={formData.officerZip} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
            </div>
          </div>
        </div>

        {/* Section 4: Directors Info */}
        <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-dark-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">4</span>
              Directors Information
            </h3>
            <button type="button" onClick={addDirector} className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-2">
              <FaUsers /> Add Director
            </button>
          </div>
          <div className="space-y-6">
            {formData.directors.map((director, index) => (
              <div key={index} className="p-5 rounded-2xl bg-gray-50/50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-700 relative group">
                {formData.directors.length > 1 && (
                  <button type="button" onClick={() => removeDirector(index)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors">
                    <FaTimes />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">First Name</label>
                    <input type="text" value={director.firstName} onChange={(e) => handleDirectorChange(index, 'firstName', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Middle Name</label>
                    <input type="text" value={director.middleName} onChange={(e) => handleDirectorChange(index, 'middleName', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Last Name</label>
                    <input type="text" value={director.lastName} onChange={(e) => handleDirectorChange(index, 'lastName', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" required />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Street Address</label>
                    <input type="text" value={director.street} onChange={(e) => handleDirectorChange(index, 'street', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">City</label>
                    <input type="text" value={director.city} onChange={(e) => handleDirectorChange(index, 'city', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">State</label>
                    <input type="text" value={director.state} onChange={(e) => handleDirectorChange(index, 'state', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Zip Code</label>
                    <input type="text" value={director.zip} onChange={(e) => handleDirectorChange(index, 'zip', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm" required />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 5: Authorization */}
        <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center text-sm font-bold">5</span>
            Authorization Person
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">First Name</label>
              <input type="text" name="authFirstName" value={formData.authFirstName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Middle Name</label>
              <input type="text" name="authMiddleName" value={formData.authMiddleName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Last Name</label>
              <input type="text" name="authLastName" value={formData.authLastName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Title</label>
              <input type="text" name="authTitle" value={formData.authTitle} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Street Address</label>
              <input type="text" name="authStreet" value={formData.authStreet} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City</label>
              <input type="text" name="authCity" value={formData.authCity} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">State</label>
              <input type="text" name="authState" value={formData.authState} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zip Code</label>
              <input type="text" name="authZip" value={formData.authZip} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none" required />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-10 border-t border-gray-100 dark:border-dark-700">
          <button type="button" onClick={onCancel} className="px-8 py-3 rounded-xl font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all text-center text-sm">Cancel</button>
          <button type="button" onClick={() => onSave(formData, false)} disabled={isSubmitting} className="px-8 py-3 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm">
            <FaSave /> Save as Draft
          </button>
          <button type="submit" disabled={isSubmitting} className="px-10 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm">
            {isSubmitting ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Submitting...</> : <><FaFilePdf /> Submit Delaware C-CORP Checklist</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DelawareCCorpChecklistForm;
